import { CustomCondition } from '@administration/codelists/codelists.interface';
import { Component, effect, Input, OnChanges, OnDestroy, OnInit, signal, SimpleChanges } from '@angular/core';
import { CodeList } from '@common/classes/codelist';
import { CodelistPipe } from '@common/pipes/codelist.pipe';
import { CommonService } from '@common/services/common.service';
import { QueryService } from '@common/services/query.service';
import { environment } from '@environments/environment';
import { takeUntilResultFinalize, tapSuccessResult } from '@ngneat/query';
import { debounce } from 'lodash';
import { firstValueFrom, lastValueFrom, Subject } from 'rxjs';
import { BaseInputComponent } from '../base-input/base-input.component';
import { CodelistInputConfig } from '../input.type';

export type CodelistLabelFormat = 'LongId' | 'LongCode' | 'OnlyId' | 'OnlyName' | 'OnlyCode';

/**
 * Codelist input component.
 * @param codelist - The codelist name.
 * @param isMultiple - Whether the input is multiple.
 * @param textField - The text field of the codelist.
 * @param valueField - The value field of the codelist.
 * @param popupSettings - The popup settings of the dropdown.
 * @param labelFormat - Predfined format or custom format using function.
 * @param customFilter - The custom filter function.
 * @param customUrl - Custom URL endpoint to override the default codelist query.
 * @param isRequiredIndicatorVisible - Whether the input shows red asterisk for mandatory GUI field (and does NOT enforce mandatory entry)
 */
@Component({
    selector: 'app-codelist-input',
    templateUrl: './codelist-input.component.html',
    providers: [{ provide: BaseInputComponent, useExisting: CodelistInputComponent }]
})
export class CodelistInputComponent
    extends BaseInputComponent<string | string[]>
    implements OnInit, OnDestroy, OnChanges
{
    @Input({ required: true }) codelist: CodelistInputConfig['codelist'];
    @Input() isMultiple: CodelistInputConfig['isMultiple'];
    @Input() textField: CodelistInputConfig['textField'] = 'label';
    @Input() valueField: CodelistInputConfig['valueField'] = 'value';
    @Input() popupSettings: CodelistInputConfig['popupSettings'] =
        environment.settings.appControl.dropdown.popupSettings;
    @Input() labelFormat: CodelistInputConfig['labelFormat'] = (item: CodeList) => `${item.customText || item.name}`;
    @Input() customFilter: CodelistInputConfig['customFilter'];
    @Input() customCondition: CodelistInputConfig['customCondition'];
    @Input() customUrl: string;
    @Input() isRequiredIndicatorVisible: boolean = false;

    query$;
    options = signal<{ value: string; label: string; [key: string]: any }[]>([]);
    filteredOptions = signal<{ value: string; label: string; [key: string]: any }[]>([]);
    readonly codelistTake = environment.settings.appControl.codelist.take;
    formatedSelectedOptions = '';
    private destroy$ = new Subject<boolean>();
    filter = signal('');

    constructor(
        private queryService: QueryService,
        private codelistPipe: CodelistPipe,
        private commonService: CommonService
    ) {
        super();
        effect(async () => {
            this.formatedSelectedOptions = await this.formatSelectedOptions(this.value());
        });
    }

    ngOnInit() {
        this.queryCodelist('');
    }

    ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    // TODO: Consider replacing customConditions with a signal input
    ngOnChanges(changes: SimpleChanges) {
        if (changes['customCondition']) {
            if (
                !this.compareCustomConditions(
                    changes['customCondition'].currentValue,
                    changes['customCondition'].previousValue
                )
            ) {
                this.queryCodelist(this.filter(), this.customCondition, this.customFilter);
            }
        }

        if (changes['customFilter']) {
            if (changes['customFilter'].currentValue !== changes['customFilter'].previousValue) {
                this.queryCodelist(this.filter(), this.customCondition, this.customFilter);
            }
        }

        if (changes['customUrl']) {
            if (changes['customUrl'].currentValue !== changes['customUrl'].previousValue) {
                this.queryCodelist(this.filter(), this.customCondition, this.customFilter);
            }
        }
    }

    compareCustomConditions(a: CustomCondition[], b: CustomCondition[]) {
        if (a == undefined || b == undefined) return true;
        if (JSON.stringify(a) !== JSON.stringify(b)) return false;
        return true;
    }

    queryCodelist(
        filter,
        customCondition: CustomCondition[] = this.customCondition,
        customFilter: CodelistInputConfig['customFilter'] = this.customFilter
    ) {
        const queryParams = this.buildQueryParams(filter, customCondition, customFilter);
        this.query$ = this.executeQuery(queryParams);

        this.query$
            .pipe(
                tapSuccessResult((data: []) => {
                    this.options.set(
                        data.map((x: any) => ({
                            label: this.getCodelistLabelFormat(this.labelFormat, x),
                            value: x.id,
                            name: x.name
                        }))
                    );

                    const currentValue = this.isMultiple ? this.value()?.[0] : this.value();
                    if (customFilter && typeof customFilter === 'function') {
                        const filterFunction = customFilter;
                        this.filteredOptions.set(this.options().filter((x: any) => filterFunction(x, filter)));
                        return;
                    }
                    this.filteredOptions.set(
                        this.options().filter((x, i) => x.value === currentValue || i < this.codelistTake + 1)
                    );
                }),
                takeUntilResultFinalize()
            )
            .subscribe();
    }

    queryCodelistDebounced = debounce((filter) => {
        this.queryCodelist(filter);
        this.filter.set(filter);
    }, 500);

    async formatSelectedOptions(selected: string | string[]): Promise<string> {
        if (!this.options() || !selected) return null;

        if (this.isMultiple) {
            if (!Array.isArray(selected) || selected.length === 0) {
                return null;
            }
            return await Promise.all(
                selected.map(
                    async (item) => await firstValueFrom(this.codelistPipe.transform(item, this.codelist, false))
                )
            ).then((res) => res.join(', '));
        } else {
            if (Array.isArray(selected)) return null;
            return await lastValueFrom(this.codelistPipe.transform(selected, this.codelist, false));
        }
    }

    private getCodelistLabelFormat(labelFormat: CodelistInputConfig['labelFormat'], item?: CodeList) {
        if (typeof labelFormat === 'function') {
            return labelFormat(item);
        }
        switch (labelFormat) {
            case 'LongCode':
                return `${item.code} - ${item.customText || item.name}`;
            case 'LongId':
                return `${item.id} - ${item.customText || item.name}`;
            case 'OnlyId':
                return `${item.id}`;
            case 'OnlyCode':
                return `${item.code}`;
            case 'OnlyName':
                return `${item.name || item.customText}`;
            default:
                return `${item.customText || item.name}`;
        }
    }

    private buildQueryParams(
        filter: string,
        customCondition: CustomCondition[] = this.customCondition,
        customFilter: CodelistInputConfig['customFilter'] = this.customFilter
    ) {
        return {
            name: this.codelist,
            filter,
            customFilter: typeof customFilter == 'string' ? customFilter : '',
            customCondition: customCondition,
            selectedIds: this.value ? [this.value()].flat() : null,
            take: this.isEditMode ? this.codelistTake : 1
        };
    }

    private executeQuery(queryParams: any) {
        if (this.customUrl) {
            // Use custom URL with the generic getQuery method
            const queryName = this.customUrl.split('/').pop();
            return this.queryService.getQuery(queryName, queryParams, {
                injector: this.commonService.injector
            }).result$;
        } else {
            // Use the default codelist query method
            return this.queryService.getCodelistQuery({ ...queryParams, injector: this.commonService.injector })
                .result$;
        }
    }
}
